import {
  ArrowRight2,
  Edit2,
  type Icon,
  Lock,
  Logout,
  Message,
  Notification,
  Setting2,
  ShieldTick,
  Star1,
} from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData } from 'react-router';
import QrCodeImage from '@/assets/images/icons/qr-code.svg?react';
import AppCell from '@/components/common/app-cell';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import UserHero from '@/components/user/user-hero';
import useUserPlatform from '@/hooks/use-user-platform';
import useSignOut from '@/hooks/users/use-sign-out';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';
import { makePrompt } from '@/stores/prompter';

export default function MyIndexRoute() {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const { data: userInfo } = useUserInfo();
  const { mutateAsync: signOut } = useSignOut();
  const { isEmbedded } = useUserPlatform();

  const menuItems: Readonly<Array<{ key: string; icon: Icon; href: string; hidden?: boolean }>> = [
    { icon: Star1, key: 'collections', href: ROUTES.MY.COLLECTIONS.$path() },
    { icon: Notification, key: 'notifications', href: ROUTES.MY.SETTINGS.NOTIFICATIONS.$path() },
    { icon: Message, key: 'messaging', href: ROUTES.MY.SETTINGS.MESSAGING.$path() },
    { icon: Lock, key: 'privacy', href: ROUTES.MY.SETTINGS.PRIVACY.$path() },
    { icon: ShieldTick, key: 'security', href: ROUTES.MY.SETTINGS.SECURITY.$path(), hidden: isEmbedded },
    { icon: Setting2, key: 'general', href: ROUTES.MY.SETTINGS.GENERAL.$path() },
  ];

  const handleSignOut = () =>
    makePrompt({
      key: 'sign-out',
      title: t('general-settings.log-out-confirm'),
      promise: () => signOut(),
    });

  return (
    <PageLayout
      title={t('mine.title')}
      headerConfig={{ navigateBack: isEmbedded }}
      className='gap-0 flex size-full desktop:flex-1 pb-safe-offset-16 desktop:pb-0'
      blockOnDesktop>
      <div className='mx-5 flex flex-col gap-3'>
        <LoadingLayout
          className='min-h-42'
          promise={loaderData?.promises}>
          {() => (
            <UserHero
              headImg={userInfo?.HeadImg}
              nickname={userInfo?.NickName}
              sign={userInfo?.Sign}
              addOn={
                <>
                  <Link
                    to={ROUTES.MY.QR_CODE.$path()}
                    className='btn btn-square btn-ghost absolute left-0 top-0'>
                    <QrCodeImage className='size-5' />
                  </Link>

                  <Link
                    to={ROUTES.MY.INFO.$path()}
                    className='btn btn-square btn-ghost absolute right-0 top-0'>
                    <Edit2
                      variant='Bold'
                      className='size-5 text-white'
                      size={20}
                    />
                  </Link>
                </>
              }
            />
          )}
        </LoadingLayout>

        <div className='flex flex-col'>
          {menuItems
            .filter((item) => item.hidden !== true)
            .map((item) => (
              <Link
                key={item.key}
                to={item.href}>
                <AppCell
                  className='px-0 py-2'
                  label={t(`mine.${item.key}`)}
                  prefix={
                    <item.icon
                      variant='Outline'
                      size={20}
                      className='size-8 rounded-lg p-1.5'
                    />
                  }
                  suffix={
                    <ArrowRight2
                      size={16}
                      variant='Outline'
                      className='size-4'
                    />
                  }
                />
              </Link>
            ))}

          {!isEmbedded && (
            <>
              <hr className='my-2' />

              <AppCell
                className='px-0 py-2 text-error'
                label={t('general-settings.log-out')}
                prefix={
                  <Logout
                    variant='Outline'
                    size={20}
                    className='size-8 rounded-lg p-1.5'
                  />
                }
                onClick={handleSignOut}
              />
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
